import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api, { formatApiError } from "@/lib/api";
import { SupportThread, CAT_LABELS } from "@/components/SupportThread";
import { Loader2, Plus, LifeBuoy, ArrowLeft, Paperclip, X, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["billing", "technical", "account", "content", "other"];

function NewTicket({ onCreated, onCancel, initial }) {
  const [subject, setSubject] = useState(initial?.subject || "");
  const [category, setCategory] = useState(initial?.category || "technical");
  const [message, setMessage] = useState(initial?.message || "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and a message."); return; }
    setSaving(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/support/upload", fd);
        attachment_id = up.data.id;
      }
      const { data } = await api.post("/support/tickets", { subject: subject.trim(), category, message: message.trim(), attachment_id });
      toast.success("Ticket sent — we'll get back to you soon.");
      onCreated(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send ticket");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="support-new-form">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl">New support ticket</h2>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
      </div>
      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Subject</label>
      <input data-testid="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
        placeholder="Brief summary of your issue"
        className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary text-sm mb-4" />

      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Category</label>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="support-categories">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`support-cat-${c}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${category === c ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary"}`}>
            {CAT_LABELS[c]}
          </button>
        ))}
      </div>

      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Message</label>
      <textarea data-testid="support-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
        placeholder="Describe what's happening. Include steps to reproduce if it's a bug."
        className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary text-sm resize-none mb-4" />

      <div className="flex items-center gap-3 mb-5">
        <input ref={fileRef} type="file" className="hidden" data-testid="support-new-file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx,.csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button onClick={() => fileRef.current?.click()} data-testid="support-new-attach"
          className="flex items-center gap-2 border border-border px-3 py-2 rounded-sm text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
          <Paperclip className="w-4 h-4" /> Attach a screenshot/file
        </button>
        {file && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{file.name}</span>}
      </div>

      <button onClick={submit} disabled={saving} data-testid="support-submit"
        className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send ticket
      </button>
    </div>
  );
}

export default function Support() {
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(searchParams.get("subject") ? "new" : "list"); // "list" | "new" | <ticketId>
  const prefill = searchParams.get("subject")
    ? { subject: searchParams.get("subject"), category: searchParams.get("category") || "content", message: searchParams.get("message") || "" }
    : null;

  const load = () => {
    setLoading(true);
    api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-6 py-12" data-testid="support-page">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-widest font-mono text-primary flex items-center gap-2"><LifeBuoy className="w-4 h-4" /> Support</span>
            <h1 className="font-serif text-5xl mt-2">Get help</h1>
            <p className="text-sm text-muted-foreground mt-2">Send us a message and track the conversation right here.</p>
          </div>
          {view === "list" && (
            <button onClick={() => setView("new")} data-testid="support-new-ticket-btn"
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
              <Plus className="w-4 h-4" /> New ticket
            </button>
          )}
          {view !== "list" && (
            <button onClick={() => { setView("list"); load(); }} data-testid="support-back"
              className="flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" /> All tickets
            </button>
          )}
        </div>

        {view === "new" && <NewTicket initial={prefill} onCreated={(id) => { load(); setView(id); }} onCancel={() => setView("list")} />}

        {view !== "new" && view !== "list" && (
          <div className="bg-card border border-border rounded-md p-6 h-[70vh]">
            <SupportThread ticketId={view} onChanged={load} />
          </div>
        )}

        {view === "list" && (
          loading ? (
            <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-border rounded-md" data-testid="support-empty">
              <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">You haven't opened any tickets yet.</p>
              <button onClick={() => setView("new")} className="mt-4 text-primary hover:underline text-sm">Open your first ticket →</button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="support-ticket-list">
              {tickets.map((t) => (
                <button key={t.id} onClick={() => setView(t.id)} data-testid={`support-ticket-${t.id}`}
                  className="w-full text-left bg-card border border-border rounded-md p-4 hover:border-primary/50 transition-colors flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{t.subject}</span>
                      {t.unread_user && <span className="w-2 h-2 rounded-full bg-primary shrink-0" title="New reply" />}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{CAT_LABELS[t.category] || "Other"} · {t.message_count} message{t.message_count === 1 ? "" : "s"} · {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString() : ""}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full border shrink-0 ${t.status === "answered" ? "text-emerald-400 border-emerald-500/40" : t.status === "closed" ? "text-muted-foreground border-border" : "text-primary border-primary/50"}`}>{t.status}</span>
                </button>
              ))}
            </div>
          )
        )}
      </div>
    </PageShell>
  );
}
